<vs-dialog
  #previewDialog
  [config]="{
      dialogContainerClass: 'w-[700px] sm:w-[500px] max-w-[700px]',
      disableBackdropDismiss: false,
      hideBackdrop: false
    }"
>
  <vs-dialog-header
    [config]="{
        mode: 'simple',
        hideCloseButton: false,

        icon: 'tag-03',
        iconTheme: 'primary',

        title: 'Plan Add-on Configuration',
        subtitle: 'Create and manage add-ons and configure prerequisite relationships.'
      }"
  ></vs-dialog-header>

  <div class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
    <!-- ═══════ Section: Base Add-ons ═══════ -->
    <div class="flex gap-2">
      <vs-divider class="self-center"></vs-divider>
      <span class="min-w-max text-sm font-medium text-gray-700"> Base Add-ons </span>
      <vs-divider class="self-center"></vs-divider>
    </div>
    <p class="text-xs text-gray-500 -mt-2">Selectable independently</p>

    <div class="flex flex-col gap-1">
      <div
        *ngFor="let addon of baseAddons"
        class="flex items-center justify-between rounded-md px-3 py-2 hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-colors"
        (click)="openAddonDialog(addon)"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-gray-900 truncate">{{ addon.title }}</span>
          <span *ngIf="addon.description" class="text-xs text-gray-500 truncate">{{ addon.description }}</span>
        </div>
        <button
          *ngIf="policy?.status === 'DRAFT'"
          vs-icon-button
          size="sm"
          appearance="clear"
          theme="danger"
          (click)="$event.stopImmediatePropagation(); removeAddonCheck(addon)"
        >
          <vs-icon name="trash-01" size="16"></vs-icon>
        </button>
      </div>

      <p *ngIf="!baseAddons.length" class="text-xs text-gray-400 italic px-3">No base add-ons configured.</p>
    </div>

    <!-- ═══════ Section: Add-ons With Prerequisites ═══════ -->
    <div class="flex gap-2">
      <vs-divider class="self-center"></vs-divider>
      <span class="min-w-max text-sm font-medium text-gray-700"> Add-ons With Prerequisites </span>
      <vs-divider class="self-center"></vs-divider>
    </div>
    <p class="text-xs text-gray-500 -mt-2">Unlocked when required add-ons are selected</p>

    <div class="flex flex-col gap-1">
      <div
        *ngFor="let addon of dependentAddons"
        class="flex items-start justify-between rounded-md px-3 py-2 hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-colors"
        (click)="openAddonDialog(addon)"
      >
        <div class="flex flex-col min-w-0 gap-0.5">
          <div class="flex items-center gap-1.5">
            <vs-icon name="link-03" size="14" class="text-gray-400 flex-shrink-0"></vs-icon>
            <span class="text-sm font-medium text-gray-900 truncate">{{ addon.title }}</span>
          </div>
          <span *ngIf="addon.description" class="text-xs text-gray-500 truncate ml-5">{{ addon.description }}</span>
          <span class="text-xs text-gray-400 ml-5 mt-0.5">
            Requires {{ addon.dependency?.operator === 'OR' ? 'any' : 'all' }} of: {{
            getPrerequisiteTitles(addon).join(', ') }}
          </span>
        </div>
        <button
          *ngIf="policy?.status === 'DRAFT'"
          vs-icon-button
          size="sm"
          appearance="clear"
          theme="danger"
          class="flex-shrink-0 mt-0.5"
          (click)="$event.stopImmediatePropagation(); removeAddonCheck(addon)"
        >
          <vs-icon name="trash-01" size="16"></vs-icon>
        </button>
      </div>

      <p *ngIf="!dependentAddons.length" class="text-xs text-gray-400 italic px-3">No dependent add-ons configured.</p>
    </div>

    <button vs-button appearance="clear" (click)="openAddonDialog()" *ngIf="policy?.status === 'DRAFT'">
      <vs-icon name="plus"></vs-icon> Create new add-on
    </button>

    <!-- ═══════ Section: Aggregate Limits ═══════ -->
    <div class="flex gap-2">
      <vs-divider class="self-center"></vs-divider>
      <span class="min-w-max"> Aggregate Limits </span>
      <vs-divider class="self-center"></vs-divider>
    </div>

    <div class="flex gap-2 flex-wrap">
      <vs-badge
        *ngFor="let liability of plan?.liabilityGroups"
        theme="default"
        (click)="openLiabilityGroupDialog(liability)"
      >
        {{ liability.title }}
        <vs-icon
          *ngIf="policy?.status === 'DRAFT'"
          name="x"
          (click)="$event.stopImmediatePropagation(); removeLiabilityGroupCheck(liability)"
        ></vs-icon>
      </vs-badge>
    </div>

    <button vs-button appearance="clear" (click)="openLiabilityGroupDialog()" *ngIf="policy?.status === 'DRAFT'">
      <vs-icon name="plus"></vs-icon> Create new Aggregate Limit group
    </button>
  </div>

  <div class="flex justify-end mt-4">
    <button vs-button appearance="outline" (click)="close()">Cancel</button>
  </div>
</vs-dialog>

<vs-dialog #createAddonDialog>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      
      icon: 'plus',
      iconTheme: 'primary',

      title: 'Create Included In Group',
      subtitle: 'Enter the details for the group below.'
    }"
    (close)="closeAddonDialog()"
  ></vs-dialog-header>

  <form class="flex flex-col gap-4 mb-4" [formGroup]="addonForm">
    <vs-form-field>
      <label>Title</label>
      <input vsInput type="text" formControlName="title" />
    </vs-form-field>

    <vs-form-field>
      <label>Description</label>
      <input vsInput type="text" formControlName="description" />
    </vs-form-field>

    <div class="flex gap-4">
      <input vsInput type="checkbox" appearance="toggle" id="addon-is-default" formControlName="isDefault" />

      <div class="flex flex-col">
        <label for="addon-is-default" class="text-sm font-medium text-gray-700">Selected by default</label>
        <label for="addon-is-default" class="text-sm text-gray-600">
          Pre-selected in checkout. Customers can still remove it.
        </label>
      </div>
    </div>

    <!-- Dependency Configuration -->
    <ng-container *ngIf="availableDependencyAddons.length > 0">
      <vs-divider class="my-2"></vs-divider>

      <div class="flex gap-4">
        <input vsInput type="checkbox" appearance="toggle" id="dep-enabled" formControlName="dependencyEnabled" />

        <div class="flex flex-col">
          <label for="dep-enabled" class="text-sm font-medium text-gray-700">Requires other add-ons</label>
          <label for="dep-enabled" class="text-sm text-gray-600">
            Enable to add prerequisite add-ons for this add-on.
          </label>
        </div>
      </div>

      <ng-container *ngIf="addonForm.value.dependencyEnabled">
        <vs-form-field>
          <label>Prerequisite add-ons</label>
          <vs-select vsInput [config]="{ multiple: true }" formControlName="dependencyAddonIds">
            <li *ngFor="let dep of availableDependencyAddons" vs-select-option class="mb-2" [value]="dep.id">
              {{ dep.title }}
            </li>
          </vs-select>
        </vs-form-field>

        <vs-form-field>
          <label>Requirement type</label>
          <vs-select vsInput formControlName="dependencyOperator">
            <li vs-select-option value="AND" class="mb-2">All prerequisites required</li>
            <li vs-select-option value="OR" class="mb-2">Any prerequisite required</li>
          </vs-select>
        </vs-form-field>
      </ng-container>
    </ng-container>
  </form>

  <div class="footer">
    <div class="buttons flex gap-4">
      <button class="flex-grow" vs-button size="md" appearance="outline" (click)="closeAddonDialog()">Cancel</button>

      <button
        *ngIf="policy?.status === 'DRAFT'"
        class="flex-grow"
        vs-button
        size="md"
        theme="primary"
        [disabled]="!addonForm.valid"
        (click)="createAddon()"
      >
        {{ selectedAddon ? 'Update' : 'Create' }}
      </button>
    </div>
  </div>
</vs-dialog>

<vs-dialog #removeAddonDialog>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      
      icon: 'plus',
      iconTheme: 'primary',

      title: 'Remove Included In Group?',
      subtitle: 'Are you sure you want to remove ' + (selectedAddon?.title ?? 'this included in group') + '? Doing so will also delete all coverage config blocks where it is used.'
    }"
    (close)="closeAddonDialog()"
  ></vs-dialog-header>

  <div class="footer">
    <div class="buttons flex gap-4">
      <button class="flex-grow" vs-button size="md" appearance="outline" (click)="removeAddonDialog.close()">
        Cancel
      </button>

      <button class="flex-grow" vs-button size="md" theme="danger" (click)="removeAddon()">Remove</button>
    </div>
  </div>
</vs-dialog>

<vs-dialog #createLiabilityGroupDialog>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      
      icon: 'plus',
      iconTheme: 'primary',

      title: 'Create Aggregate Group',
      subtitle: 'Enter the details for the group below.'
    }"
    (close)="closeAddonDialog()"
  ></vs-dialog-header>

  <form class="flex flex-col gap-4 mb-4" [formGroup]="liabilityGroupForm">
    <vs-form-field>
      <label>Title</label>
      <input vsInput type="text" formControlName="title" />
    </vs-form-field>

    <vs-form-field>
      <label>Description</label>
      <input vsInput type="text" formControlName="description" />
    </vs-form-field>

    <vs-form-field [config]="{ label: 'Limit' }">
      <vs-input-container>
        <!-- Value Property Badge -->
        <div *ngIf="liabilityGroupForm.value.limitPropertyPath; else limitInput" class="w-fit max-w-[128px] pt-[5px]">
          <vs-badge
            theme="default"
            [vsTooltip]="liabilityGroupForm.value.limitPropertyPath"
            tooltipPosition="top"
            class="w-full h-4"
          >
            <span class="w-full truncate"
              >{{ liabilityGroupForm.value.limitPropertyPath | valuePathLabel: propertySet | async }}</span
            >
            <vs-icon
              name="x-close"
              slot="end"
              class="cursor-pointer"
              (click)="setLimitPropertyPath(null); limitPropertyPicker.setValue(null)"
            ></vs-icon>
          </vs-badge>
        </div>
        <ng-template #limitInput>
          <div class="flex flex-row w-full h-full gap-2 justify-center align-middle items-center">
            <p class="text-gray-600">$</p>
            <input type="text" mask="separator.2" thousandSeparator="," formControlName="limit" />
          </div>
        </ng-template>

        <ws-condition-value-property-picker
          #limitPropertyPicker
          class="pt-1"
          slot="end"
          [propertySet]="propertySet"
          [type]="num"
          format="currency"
          [selectedPropertyPath]="liabilityGroupForm.value.limitPropertyPath"
          (selectionChanged)="setLimitPropertyPath($event.property)"
        ></ws-condition-value-property-picker>
      </vs-input-container>
    </vs-form-field>
  </form>

  <div class="footer">
    <div class="buttons flex gap-4">
      <button class="flex-grow" vs-button size="md" appearance="outline" (click)="closeLiabilityGroupDialog()">
        Cancel
      </button>

      <button
        *ngIf="policy?.status === 'DRAFT'"
        class="flex-grow"
        vs-button
        size="md"
        theme="primary"
        [disabled]="((liabilityGroupForm.value.limit === null || liabilityGroupForm.value.limit === undefined || $any(liabilityGroupForm.value.limit) === '') && !liabilityGroupForm.value.limitPropertyPath) || !liabilityGroupForm.valid"
        (click)="createLiabilityGroup()"
      >
        {{ selectedLiabilityGroup ? 'Update' : 'Create' }}
      </button>
    </div>
  </div>
</vs-dialog>

<vs-dialog #removeLiabilityGroupDialog>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: false,
      
      icon: 'plus',
      iconTheme: 'primary',

      title: 'Remove Aggregate Group?',
      subtitle: 'Are you sure you want to remove ' + (selectedLiabilityGroup?.title ?? 'this included in group') + '? Doing so will also delete all coverage config blocks where it is used.'
    }"
    (close)="closeAddonDialog()"
  ></vs-dialog-header>

  <div class="footer">
    <div class="buttons flex gap-4">
      <button class="flex-grow" vs-button size="md" appearance="outline" (click)="removeLiabilityGroupDialog.close()">
        Cancel
      </button>

      <button class="flex-grow" vs-button size="md" theme="danger" (click)="removeLiabilityGroup()">Remove</button>
    </div>
  </div>
</vs-dialog>
